'use client';

import React, { useState, useEffect } from 'react';
import { useRole } from '@/contexts/RoleContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { createClient } from '@/lib/supabase/client';
import { Truck, MapPin, Phone, CheckCircle, Clock, LogOut, RefreshCw, Navigation, Package } from 'lucide-react';

interface Delivery {
  id: number;
  retailer_name: string;
  delivery_address: string;
  delivery_time: string;
  status: string;
  total_amount: number;
  phone: string;
}

export default function MobileDriverView() {
  const { activeStaff, logoutRole } = useRole();
  const { language, dir } = useLanguage();
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'delivered'>('all');

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('delivery_schedules')
      .select('id, retailer_name, delivery_address, delivery_time, status, total_amount, phone')
      .order('delivery_time')
      .then(({ data }) => {
        setDeliveries(data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = deliveries.filter((d) => {
    if (filter === 'pending') return d.status !== 'delivered';
    if (filter === 'delivered') return d.status === 'delivered';
    return true;
  });

  const pendingCount = deliveries.filter((d) => d.status !== 'delivered').length;
  const deliveredCount = deliveries.filter((d) => d.status === 'delivered').length;

  const getStatusIcon = (status: string) => {
    if (status === 'delivered') return <CheckCircle size={14} className="text-green-500" />;
    if (status === 'in_transit') return <Truck size={14} className="text-blue-500" />;
    return <Clock size={14} className="text-amber-500" />;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, { ar: string; fr: string }> = {
      delivered: { ar: 'تم التوصيل', fr: 'Livré' },
      in_transit: { ar: 'في الطريق', fr: 'En route' },
      pending: { ar: 'قيد الانتظار', fr: 'En attente' },
      scheduled: { ar: 'مجدول', fr: 'Planifié' },
    };
    return labels[status]?.[language] || status;
  };

  const markDelivered = async (id: number) => {
    const supabase = createClient();
    await supabase.from('delivery_schedules').update({ status: 'delivered' }).eq('id', id);
    setDeliveries((prev) => prev.map((d) => (d.id === id ? { ...d, status: 'delivered' } : d)));
  };

  return (
    <div className="min-h-screen bg-background flex flex-col max-w-md mx-auto" dir={dir}>
      {/* Header */}
      <div className="bg-green-600 text-white px-4 pt-safe-top pb-4 sticky top-0 z-20">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs opacity-80">{language === 'ar' ? 'السائق' : 'Chauffeur'}</p>
            <h1 className="text-base font-800">{activeStaff?.staffName}</h1>
          </div>
          <button
            onClick={logoutRole}
            className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <LogOut size={15} />
          </button>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/15 rounded-xl p-2.5 text-center">
            <p className="text-lg font-800">{deliveries.length}</p>
            <p className="text-2xs opacity-80">{language === 'ar' ? 'إجمالي' : 'Total'}</p>
          </div>
          <div className="bg-white/15 rounded-xl p-2.5 text-center">
            <p className="text-lg font-800">{pendingCount}</p>
            <p className="text-2xs opacity-80">{language === 'ar' ? 'متبقي' : 'Restant'}</p>
          </div>
          <div className="bg-white/15 rounded-xl p-2.5 text-center">
            <p className="text-lg font-800">{deliveredCount}</p>
            <p className="text-2xs opacity-80">{language === 'ar' ? 'تم' : 'Livré'}</p>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex bg-white border-b border-border sticky top-[120px] z-10">
        {[
          { key: 'all', labelAr: 'الكل', labelFr: 'Tout' },
          { key: 'pending', labelAr: 'المتبقي', labelFr: 'En attente' },
          { key: 'delivered', labelAr: 'تم التوصيل', labelFr: 'Livré' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key as any)}
            className={`flex-1 py-3 text-xs font-600 transition-colors border-b-2 ${
              filter === tab.key
                ? 'border-green-500 text-green-600' :'border-transparent text-muted-foreground'
            }`}
          >
            {language === 'ar' ? tab.labelAr : tab.labelFr}
          </button>
        ))}
      </div>

      {/* Delivery list */}
      <div className="flex-1 overflow-y-auto p-4 pb-safe-bottom space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={20} className="animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Truck size={40} className="text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-sm text-muted-foreground">
              {language === 'ar' ? 'لا توجد توصيلات' : 'Aucune livraison'}
            </p>
          </div>
        ) : (
          filtered.map((delivery, idx) => (
            <div
              key={delivery.id}
              className={`bg-white rounded-2xl border shadow-sm p-4 ${
                delivery.status === 'delivered' ? 'border-green-200 opacity-70' : 'border-border'
              }`}
            >
              {/* Stop number + status */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-green-100 border border-green-300 flex items-center justify-center">
                    <span className="text-2xs font-800 text-green-700">{idx + 1}</span>
                  </div>
                  <h3 className="text-sm font-700 text-foreground">{delivery.retailer_name}</h3>
                </div>
                <div className="flex items-center gap-1">
                  {getStatusIcon(delivery.status)}
                  <span className="text-2xs font-600 text-muted-foreground">
                    {getStatusLabel(delivery.status)}
                  </span>
                </div>
              </div>

              {/* Address */}
              {delivery.delivery_address && (
                <div className="flex items-start gap-1.5 mb-2">
                  <MapPin size={12} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">{delivery.delivery_address}</p>
                </div>
              )}

              {/* Time + Amount */}
              <div className="flex items-center gap-3 mb-3">
                {delivery.delivery_time && (
                  <div className="flex items-center gap-1">
                    <Clock size={11} className="text-muted-foreground" />
                    <span className="text-2xs text-muted-foreground">{delivery.delivery_time}</span>
                  </div>
                )}
                {delivery.total_amount > 0 && (
                  <div className="flex items-center gap-1">
                    <Package size={11} className="text-muted-foreground" />
                    <span className="text-2xs font-700 text-foreground">
                      {delivery.total_amount.toLocaleString()} MAD
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {delivery.phone && (
                  <a
                    href={`tel:${delivery.phone}`}
                    className="flex-1 flex items-center justify-center gap-1.5 text-2xs font-600 text-blue-600 bg-blue-50 border border-blue-200 py-2 rounded-xl"
                  >
                    <Phone size={12} />
                    {language === 'ar' ? 'اتصال' : 'Appeler'}
                  </a>
                )}
                {delivery.delivery_address && (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(delivery.delivery_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1.5 text-2xs font-600 text-purple-600 bg-purple-50 border border-purple-200 py-2 rounded-xl"
                  >
                    <Navigation size={12} />
                    {language === 'ar' ? 'خريطة' : 'Carte'}
                  </a>
                )}
                {delivery.status !== 'delivered' && (
                  <button
                    onClick={() => markDelivered(delivery.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 text-2xs font-700 text-white bg-green-500 py-2 rounded-xl hover:bg-green-600 active:scale-95 transition-all"
                  >
                    <CheckCircle size={12} />
                    {language === 'ar' ? 'تم التوصيل' : 'Livré'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
